import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { PricingCard } from '../components/pricing/PricingCard';
import { ComparisonTable } from '../components/pricing/ComparisonTable';
export function WebDesign() {
  const [tableExpanded, setTableExpanded] = useState(false);
  const toggleTable = () => {
    setTableExpanded(!tableExpanded);
  };
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleBronzeClick = () => {
    window.open('https://buy.stripe.com/aFa14hfmQ5ga8v85gsaZi00', '_blank');
  };
  const handleSilverClick = () => {
    window.open('https://buy.stripe.com/eVqcMZ3E8cICbHk4coaZi01', '_blank');
  };
  const handleGoldClick = () => {
    window.open('https://buy.stripe.com/eVq3cpfmQ6ke8v86kwaZi02', '_blank');
  };
  return <>
      <Helmet data-id="element-2471">
        <title data-id="element-2472">Web Design Packages | Bright Pixxel Studio</title>
        <meta name="description" content="Web design packages for UK small businesses starting from £495. Custom, mobile-first websites built to convert. Get your free homepage design today." data-id="element-2473" />
        <link rel="canonical" href="https://bright-pixxel.com/web-design" data-id="element-2474" />
        {/* Open Graph Meta Tags */}
        <meta property="og:locale" content="en_GB" data-id="element-2475" />
        <meta property="og:type" content="website" data-id="element-2476" />
        <meta property="og:title" content="Web Design Packages | Bright Pixxel Studio" data-id="element-2477" />
        <meta property="og:description" content="Web design packages for UK small businesses starting from £495. Custom, mobile-first websites built to convert. Get your free homepage design today." data-id="element-2478" />
        <meta property="og:url" content="https://bright-pixxel.com/web-design" data-id="element-2479" />
        <meta property="og:site_name" content="Bright Pixxel Studio" data-id="element-2480" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-2481" />
        <meta property="og:image:width" content="1200" data-id="element-2482" />
        <meta property="og:image:height" content="630" data-id="element-2483" />
        <meta property="og:image:type" content="image/png" data-id="element-2484" />
        <meta property="og:image:alt" content="Bright Pixxel Studio - Professional Web Design Services" data-id="element-2485" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-2486" />
        <meta name="twitter:title" content="Web Design Packages | Bright Pixxel Studio" data-id="element-2487" />
        <meta name="twitter:description" content="Web design packages for UK small businesses starting from £495. Custom, mobile-first websites built to convert. Get your free homepage design today." data-id="element-2488" />
        <meta name="twitter:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-2489" />
        <meta name="twitter:image:alt" content="Bright Pixxel Studio - Professional Web Design Services" data-id="element-2490" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-[clamp(56px,8vw,72px)] pb-16 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2491">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2492">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2493">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-2494">
              Web Design Packages for UK Small Businesses
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-white/90 mb-8" data-id="element-2495">
              Professional, mobile-optimised websites that deliver exceptional
              user experiences. Every site we create is built with performance,
              accessibility, and SEO best practices at its core.
            </motion.p>
          </div>
        </div>
      </section>
      {/* Pricing Cards Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-2496">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2497">
          <div className="max-w-7xl mx-auto" data-id="element-2498">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2499">
              <PricingCard title="Launch" subtitle="Starter" price="£495" popular={false} features={['Up to 4 pages', 'Brand-aligned design', 'Contact form', 'Mobile-first build', 'Basic SEO setup', 'Domain connection']} iconSrc="https://images.unsplash.com/photo-1605478328994-f93e98217da7?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="Launch web design plan icon" ctaText="Get your free homepage design" onClick={scrollToContact} data-id="element-2500" />
              <PricingCard title="Standard" subtitle="Growing Business" price="£895" popular={true} features={['Up to 8 pages', 'Custom design and build', 'Content integration', 'SEO-ready structure', 'Meta titles and descriptions', 'Favicon & social links', 'Full launch setup']} iconSrc="https://images.unsplash.com/photo-1568480289356-5a75d0fd47fc?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="Standard web design plan icon" ctaText="Get your free homepage design" onClick={scrollToContact} data-id="element-2501" />
              <PricingCard title="Growth" subtitle="Full Service" price="£1,395" popular={false} features={['Up to 10 pages', 'Everything in Standard', 'Keyword-mapped page structure', 'Google Business Profile setup']} iconSrc="https://images.unsplash.com/photo-1610375461369-d613b564f4c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="Growth web design plan icon" ctaText="Get your free homepage design" onClick={scrollToContact} data-id="element-2502" />
            </div>

            <div className="mt-12 bg-[#0DC3DC]/10 border-l-4 border-[#0DC3DC] p-6 rounded-lg text-center" data-id="element-2503">
              <p className="text-gray-900 dark:text-white font-medium" data-id="element-2504">
                E-commerce and bespoke builds quoted individually — from £990.
                Additional pages £75 each.
              </p>
            </div>

            <div className="mt-16 text-left max-w-3xl mx-auto" data-id="element-2505">
              <h3 className="text-2xl font-bold mb-4 text-[#0C1B26] dark:text-white" data-id="element-2506">
                Simple, honest extras
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-2507">
                Domain registration is purchased at cost — the price depends on
                the name you choose. Hosting is £150 per year, paid to the
                hosting provider. Business email is billed yearly per mailbox.
              </p>
              <p className="text-gray-600 dark:text-gray-300" data-id="element-2508">
                Join our{' '}
                <a href="/website-care" className="text-[#0DC3DC] hover:underline font-medium" data-id="element-2509">
                  Growth care plan
                </a>{' '}
                and every yearly technical cost is covered — you never see those
                bills.
              </p>
            </div>

            <div className="mt-12 text-left max-w-3xl mx-auto bg-[#212C34] p-8 rounded-xl shadow-md" data-id="element-2510">
              <h3 className="text-2xl font-bold mb-4 text-white" data-id="element-2511">
                Every website includes ongoing care
              </h3>
              <p className="text-white/90 mb-6" data-id="element-2512">
                Every Bright Pixxel Studio website is looked after under a
                monthly care plan (from £129/month) covering security, backups
                and updates, so clients never worry about the technical side.
                Care plans are not presented as an optional extra.
              </p>
              <a href="/website-care" className="inline-flex items-center text-[#0DC3DC] hover:text-white transition-colors font-medium" data-id="element-2513">
                View our care plans{' '}
                <ChevronDownIcon className="ml-1 -rotate-90" size={16} data-id="element-2514" />
              </a>
            </div>
          </div>
        </div>
      </section>
      {/* Portfolio Section */}
      <section className="py-16 md:py-24 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2515">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2516">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-2517">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2518">
              Our Recent Projects
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-gray-600 dark:text-gray-300" data-id="element-2519">
              Take a look at some of our recent web design projects. We've
              helped businesses across various industries establish a strong
              online presence and achieve their goals.
            </motion.p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2520">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-white dark:bg-[#1E1E1E] rounded-lg overflow-hidden shadow-md flex flex-col h-full" data-id="element-2521">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pQKeoA3T9GzSKJQNGTdgoz/websites_1_-_ooOhmy.co.uk.jpg" alt="ooOhMy.co.uk e-commerce website - professional ladies fashion online store designed by Bright Pixxel Studio with modern layout and seamless shopping experience" className="w-full h-48 object-cover" loading="lazy" decoding="async" data-id="element-2522" />
              <div className="p-4 flex-grow" data-id="element-2523">
                <h3 className="text-lg font-semibold mb-1" data-id="element-2524">ooOhMy.co.uk</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-2525">
                  E-commerce / Ladies Fashion
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-2" data-id="element-2526">
                  "Bright Pixxel transformed our online store with a beautiful,
                  user-friendly design that has increased our sales by 45%.
                  Their attention to detail and e-commerce expertise made all
                  the difference."
                </p>
                <a href="https://ooohmy.co.uk" target="_blank" rel="noopener noreferrer" className="text-[#0DC3DC] text-sm hover:underline" data-id="element-2527">
                  Visit website →
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-white dark:bg-[#1E1E1E] rounded-lg overflow-hidden shadow-md flex flex-col h-full" data-id="element-2528">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/d5GzTyf2oC8ykFDj3X9wmi/Website_3_-_northamptondrivingschool.co.uk.jpg" alt="Northampton Driving School website - professional driving school website with online booking system designed by Bright Pixxel Studio UK" className="w-full h-48 object-cover" loading="lazy" decoding="async" data-id="element-2529" />
              <div className="p-4 flex-grow" data-id="element-2530">
                <h3 className="text-lg font-semibold mb-1" data-id="element-2531">
                  Northampton Driving School
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-2532">
                  Education / Driving Lessons
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-2" data-id="element-2533">
                  "Bookings increased by 60% since launching our new website.
                  The online booking system has streamlined our operations and
                  the mobile-friendly design has been perfect for our younger
                  students."
                </p>
                <a href="https://northamptondrivingschool.co.uk" target="_blank" rel="noopener noreferrer" className="text-[#0DC3DC] text-sm hover:underline" data-id="element-2534">
                  Visit website →
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="bg-white dark:bg-[#1E1E1E] rounded-lg overflow-hidden shadow-md flex flex-col h-full" data-id="element-2535">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4kYo3C4gd9QjfCrgk7a12h/Website_2_-_medicolegal-psychiatry.co.uk.jpg" alt="Medicolegal Psychiatry website - professional healthcare psychiatry practice website designed by Bright Pixxel Studio with patient-focused design" className="w-full h-48 object-cover" loading="lazy" decoding="async" data-id="element-2536" />
              <div className="p-4 flex-grow" data-id="element-2537">
                <h3 className="text-lg font-semibold mb-1" data-id="element-2538">
                  Medicolegal Partners
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-2539">
                  Healthcare / Psychiatry
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-2" data-id="element-2540">
                  "The professional site perfectly represents our practice.
                  Bright Pixxel understood our need for a sophisticated,
                  trustworthy online presence that respects patient
                  confidentiality while being informative."
                </p>
                <a href="https://medicolegal-psychiatry.co.uk" target="_blank" rel="noopener noreferrer" className="text-[#0DC3DC] text-sm hover:underline" data-id="element-2541">
                  Visit website →
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.4
          }} className="bg-white dark:bg-[#1E1E1E] rounded-lg overflow-hidden shadow-md flex flex-col h-full" data-id="element-2542">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2mYs5H4cmXngmP57UGoyEE/Website_4_-_surefix.it.com.jpg" alt="SureFix.it appliance repair website - professional home appliance repair service website with online booking designed by Bright Pixxel Studio" className="w-full h-48 object-cover" loading="lazy" decoding="async" data-id="element-2543" />
              <div className="p-4 flex-grow" data-id="element-2544">
                <h3 className="text-lg font-semibold mb-1" data-id="element-2545">SureFix.it</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-2546">
                  Home Services / Appliance Repair
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-2" data-id="element-2547">
                  "Lead generation increased 75% year-on-year after our website
                  redesign. The online booking system and service area map have
                  dramatically improved customer experience and our conversion
                  rate."
                </p>
                <a href="https://surefix.it.com" target="_blank" rel="noopener noreferrer" className="text-[#0DC3DC] text-sm hover:underline" data-id="element-2548">
                  Visit website →
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.5
          }} className="bg-white dark:bg-[#1E1E1E] rounded-lg overflow-hidden shadow-md flex flex-col h-full" data-id="element-2549">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/wqw4YWP28GPXqhAPXrrgAj/Website_5_-_axiscare.it.com.jpg" alt="AxisCare appliance repair website - professional appliance repair service website with SEO optimization designed by Bright Pixxel Studio UK" className="w-full h-48 object-cover" loading="lazy" decoding="async" data-id="element-2550" />
              <div className="p-4 flex-grow" data-id="element-2551">
                <h3 className="text-lg font-semibold mb-1" data-id="element-2552">AxisCare.it</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-2553">
                  Home Services / Appliance Repair
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-2" data-id="element-2554">
                  "ROI on our website in just 2 months! The SEO work has put us
                  on the first page of Google for our key service areas. Bright
                  Pixxel delivered exactly what we needed to grow our repair
                  business."
                </p>
                <a href="https://axiscare.it.com" target="_blank" rel="noopener noreferrer" className="text-[#0DC3DC] text-sm hover:underline" data-id="element-2555">
                  Visit website →
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-2556">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2557">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2558">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="mb-8" data-id="element-2559">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" alt="Bright Pixxel Studio - Affordable, Creative, Digital Excellence" className="h-16 mx-auto" data-id="element-2560" />
            </motion.div>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2561">
              Need a custom package?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-2562">
              We understand that every business is unique. If our standard
              packages don't quite fit your needs, we'd be happy to create a
              custom solution tailored specifically for you.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-2563">
              <Button size="lg" onClick={scrollToContact} data-id="element-2564">
                Contact Us Today
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}